import { CSSProperties, ReactNode, useState } from "react";
import useWebsiteBuilder, {
    ImageContentStyle,
    MusicContentStyle,
    VideoContentStyle,
    WebsiteBuilderContent,
    WebsiteBuilderContentType,
    WebsiteBuilderStyle
} from 'Hooks/useWebsiteBuilder';
import { clsx, makeKey, styx } from "modules/Utils";
import CollectDataPreviewContent from "./preview/CollectDataPreviewContent";
import DividerPreviewContent from "./preview/DividerPreviewContent";
import ExternalLinksPreviewContent from "./preview/ExternalLinksPreviewContent";
import ImagePreviewContent from "./preview/ImagePreviewContent";
import MusicPreviewContent from "./preview/MusicPreviewContent";
import PaymentPreviewContent from "./preview/PaymentPreviewContent";
import PresavePreviewContent from "./preview/PresavePreviewContent";
import SocialPlatformsPreviewContent from "./preview/SocialPlatformsPreviewContent";
import StreamingPlatformsPreviewContent from "./preview/StreamingPlatformsPreviewContent";
import TabsPreviewContent from "./preview/TabsPreviewContent";
import TextPreviewContent from "./preview/TextPreviewContent";
import TourPreviewContent from "./preview/TourPreviewContent";
import VideosPreviewContent from "./preview/VideoPreviewContent";
import Colors from "modules/Colors";
import SymphonyLogo from "pages/WebsitePage/components/WebsitePreview/SymphonyLogo";
import { SymphonyLogoStyleKey } from "pages/WebsitePage/components/SymphonyLogoEditorCard";
import useScreen from "Hooks/useScreen";
import { lightOrDark, whiteOrBlackTextColor } from "helpers/StyleUtils";

interface WebsiteBuilderPhonePreviewProps {
    className: string;
    style?: CSSProperties;
    websiteContent?: WebsiteBuilderContent[],
    websiteStyle?: WebsiteBuilderStyle,
    addContentButton?: ReactNode,
    isSticky?: boolean,
    onClickContentBlock?: (index: number) => void,
    isPhone?: boolean,
    isFooter?: boolean,
    isLocalTabLogic?: boolean,
}

const WebsiteBuilderPhonePreview = ({
    className,
    style,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    addContentButton,
    isSticky = true,
    onClickContentBlock,
    isPhone = true,
    isFooter = true,
    isLocalTabLogic = false,
}: WebsiteBuilderPhonePreviewProps) => {
    const {
        cssCalc,
        mobileView,
    } = useScreen();
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;

    const {
        backgroundColor,
        textColor: OLD,
        isSymphonyLogoEnabled,
        symphonyLogoStyle,
    } = websiteStyle;

    const textColor = whiteOrBlackTextColor(backgroundColor);

    const tabsContent = websiteContent.find(content => content.type === WebsiteBuilderContentType.TABS)?.tabsContent;

    const [activeTabLocal, setActiveTabLocal] = useState<string | null>(isLocalTabLogic ? (tabsContent?.activeTab || '') : null);

    const onClickTab = (tabId: string) => {
        if (!isLocalTabLogic) {
            return;
        }

        setActiveTabLocal(tabId);
    }

    const renderPreviewContent = (content: WebsiteBuilderContent, index: number) => {
        const isContentBackgroundColorEnabled = content.isContentBackgroundColorEnabled || false;
        const contentBackgroundColor = content.contentBackgroundColor || 'transparent';

        const websiteContentCurrent = websiteContent[index];

        const getPaddingTop = () => {
            const currentColor = contentBackgroundColor;
            const previousColor = websiteContent[index - 1]?.contentBackgroundColor || 'transparent';
            const isPreviousColorEnabled = websiteContent[index - 1]?.isContentBackgroundColorEnabled || false;
            const isPreviousContentDivider = websiteContent[index - 1]?.type === WebsiteBuilderContentType.DIVIDER;

            if (websiteContentCurrent.type === WebsiteBuilderContentType.DIVIDER) {
                return 0;
            }

            if (websiteContentCurrent.type === WebsiteBuilderContentType.IMAGE && websiteContentCurrent.imageContent?.style === ImageContentStyle.FULL_SCREEN) {
                return 0;
            }

            if (websiteContentCurrent.type === WebsiteBuilderContentType.VIDEO && websiteContentCurrent.videoContent?.style === VideoContentStyle.FULL_SCREEN) {
                return 0;
            }

            if (websiteContentCurrent.type === WebsiteBuilderContentType.MUSIC && websiteContentCurrent.musicContent?.style === MusicContentStyle.FULL_SCREEN) {
                return 0;
            }

            if (isPreviousContentDivider) {
                return 16;
            }

            if (index === 0) {
                return 16;
            }

            if (isContentBackgroundColorEnabled && previousColor === currentColor && isPreviousColorEnabled) {
                return 0;
            }

            if (isContentBackgroundColorEnabled) {
                return 16;
            }

            if (index > 0 && websiteContent[index - 1]?.isContentBackgroundColorEnabled) {
                return 16;
            }

            return 0;
        }

        const getPaddingBottom = () => {
            if (websiteContentCurrent.type === WebsiteBuilderContentType.DIVIDER) {
                return 0;
            }

            if (isContentBackgroundColorEnabled) {
                return 16;
            }

            return 16;
        }

        const getMarginTop = () => {
            if (
                index === 0 && !mobileView &&
                ((content.type === WebsiteBuilderContentType.IMAGE && content.imageContent?.style === ImageContentStyle.FULL_SCREEN) ||
                    (content.type === WebsiteBuilderContentType.VIDEO && content.videoContent?.style === VideoContentStyle.FULL_SCREEN))
            ) {
                return -38;
            }

            return 0;
        }

        const previewContentProps = {
            contentIndex: index,
            websiteContent,
            websiteStyle,
            onClickContentBlock: () => {
                onClickContentBlock?.(index);
            },
            onClickTab,
            activeTab: activeTabLocal,
            style: {
                backgroundColor: isContentBackgroundColorEnabled ? contentBackgroundColor : 'transparent',
                paddingTop: getPaddingTop(),
                paddingBottom: getPaddingBottom(),
                marginTop: getMarginTop(),
            }
        }

        switch (content.type) {
            case WebsiteBuilderContentType.COLLECT_DATA:
                return (
                    <CollectDataPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.DIVIDER:
                return (
                    <DividerPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.EXTERNAL_LINKS:
                return (
                    <ExternalLinksPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.IMAGE:
                return (
                    <ImagePreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.MUSIC:
                return (
                    <MusicPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.PAYMENT:
                return (
                    <PaymentPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.PRESAVE:
                return (
                    <PresavePreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.SOCIAL_PLATFORMS:
                return (
                    <SocialPlatformsPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.STREAMING_PLATFORMS:
                return (
                    <StreamingPlatformsPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.TABS:
                return (
                    <TabsPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.TEXT:
                return (
                    <TextPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.TOUR:
                return (
                    <TourPreviewContent
                        {...previewContentProps}
                    />
                )
            case WebsiteBuilderContentType.VIDEO:
                return (
                    <VideosPreviewContent
                        {...previewContentProps}
                    />
                )
            default:
                return null
        }
    }

    const renderPreviewOffsetSpacer = () => {
        const firstBackgroundColor = websiteContent[0]?.contentBackgroundColor || 'transparent';
        const firstIsContentBackgroundColorEnabled = websiteContent[0]?.isContentBackgroundColorEnabled || false;

        /** Default background color is either the provided backgroundColor or transparent */
        let backgroundColorLocal = backgroundColor || 'transparent';

        /** If first content has a custom background color enabled, use it */
        const isFirstBackgroundColorEnabledAndDifferent = firstIsContentBackgroundColorEnabled && firstBackgroundColor !== backgroundColor;

        if (isFirstBackgroundColorEnabledAndDifferent) {
            backgroundColorLocal = firstBackgroundColor;
        }

        /** Handle special case for tabs content type */
        const isFirstContentTabs = websiteContent[0]?.type === WebsiteBuilderContentType.TABS;

        if (isFirstContentTabs) {
            const activeTab = tabsContent?.tabs.find(tab => tab.id === tabsContent?.activeTab);
            const isTabColorEnabled = activeTab?.isColorEnabled || false;
            const tabColor = activeTab?.color || '#FFFFFF';

            if (isTabColorEnabled) {
                backgroundColorLocal = tabColor;
            } else if (isFirstBackgroundColorEnabledAndDifferent) {
                backgroundColorLocal = firstBackgroundColor;
            }
        }

        return (
            <div
                className="websitePreviewOffsetSpacer"
                style={styx(
                    {
                        marginTop: -38,
                        paddingTop: 38,
                        backgroundColor: backgroundColorLocal,
                    }
                )}
            />
        )
    }

    const renderSymphonyLogo = () => {
        if (!isSymphonyLogoEnabled || !isFooter) {
            return null;
        }

        switch (symphonyLogoStyle) {
            case SymphonyLogoStyleKey.BANNER:
                return (
                    <div className="symphonyLogoBanner" style={sty.symphonyLogoBanner}>
                        <SymphonyLogo
                            width={100}
                            height={100}
                            style={{ marginRight: 'auto' }}
                        />
                        <div className="symphonyLogoBannerContainer" style={{ color: 'white' }}>
                            Try free for 14 Days
                        </div>
                    </div>
                )
            case SymphonyLogoStyleKey.BELOW:
                return (
                    <div
                        className="symphonyLogoBelow"
                        style={styx(
                            sty.symphonyLogoBelow,
                            {
                                color: textColor,
                            }
                        )}
                    >
                        <div className="symphonyLogoBelowContainer">
                            Powered by
                        </div>
                        <SymphonyLogo
                            width={100}
                            height={100}
                            variant={lightOrDark(backgroundColor) === 'light' ? 'black' : 'purple'}
                        />
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div
            className={clsx(
                'websiteBuilderPhonePreviewComponent',
                className,
            )}
            style={styx(
                isPhone ? sty.phonePreview : sty.noPhonePreview,
                isPhone ?
                    null
                    :
                    {
                        minHeight: cssCalc(72 + 64),
                    },
                {
                    backgroundColor,
                    color: textColor,
                },
                !isSticky && {
                    position: 'static',
                },
                style,
            )}
        >
            {isPhone &&
                <>
                    <div className="phoneIsland" style={sty.phoneIsland} />
                    {renderPreviewOffsetSpacer()}
                </>
            }
            {addContentButton && websiteContent.length === 0 &&
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    {addContentButton}
                </div>
            }
            {websiteContent.map((content, index) => {
                if (content.type !== WebsiteBuilderContentType.TABS && content?.tabId) {
                    if (isLocalTabLogic) {
                        if (activeTabLocal !== content.tabId) {
                            return null;
                        }
                    } else {
                        if (tabsContent?.activeTab !== content.tabId) {
                            return null;
                        }
                    }
                }

                return (
                    <div
                        key={makeKey(content.type, index)}
                        className="websiteBuilderPhonePreviewContent"
                    >
                        {renderPreviewContent(content, index)}
                    </div>
                )
            })}
            {renderSymphonyLogo()}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    phonePreview: {
        position: 'sticky',
        top: 88,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 50,
        width: 350,
        height: 667,
        border: `10px solid ${Colors.grey}`,
        flexShrink: 0,
        overflowY: 'scroll',
        paddingTop: 8,
        scrollbarWidth: 'none',
    },
    noPhonePreview: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
    },
    phoneIsland: {
        position: 'sticky',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 100,
        height: 30,
        borderRadius: 100,
        backgroundColor: 'black',
        flexShrink: 0,
        zIndex: 1,
    },

    symphonyLogoBanner: {
        position: 'sticky',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: 12,
        gap: 8,
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: Colors.purpleDark,
        color: 'white',
        height: 38,
        paddingLeft: 24,
        paddingRight: 24,
        marginTop: 'auto',
        flexShrink: 0,
    },

    symphonyLogoBelow: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        paddingBottom: 16,
    },

}

export default WebsiteBuilderPhonePreview